import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { api } from '../../helpers/apiUtils';
import {
  clientLabel,
  formatBerlin,
  renderEtkp,
  renderEur,
  renderInt,
  renderRate,
  renderType,
  statusChipColor,
} from './shared';
import ExternalScoringTab from './ExternalScoringTab';
import HistoryKpiCards from './HistoryKpiCards';
import SamHtmlPreviewDialog, { SamHtmlPreviewData } from './SamHtmlPreviewDialog';

interface PlannedRow {
  id: number;
  client_code: string;
  send_date: string;
  sam_id: number;
  product_id: number;
  customer_id: string | null;
  status: string;
  mc_campaign_id: string | null;
  mc_campaign_title: string;
  dup_send: number;
  inactive_send: number;
  /** Reactivation-probe flag from external_sam_mailings. 1 = reactivation
   *  send (Inaktiv-audience probe); 0 = regular daily/cadence SAM. Drives the
   *  "Regular / Reaktivierung" sub-tab split below. */
  is_reactivation: number;
  /** external_sam_mailings.id of the DNV original when this row is the KME
   *  08:00 shadow re-send; NULL for regular sends. */
  shadow_of_id: number | null;
  product_name: string | null;
  sam_subject: string | null;
  material_type: string | null;
  asset_isin: string | null;
  asset_name: string | null;
}

interface HistoryRow extends PlannedRow {
  /** 1 = performance data structurally invalid (external incident, e.g. the
   *  DNV LP 502 outage 2026-07-20..22) — excluded from scoring + windowed
   *  KPIs, shown here with an "Ausfall" badge. */
  stats_excluded?: number;
  stats_excluded_reason?: string | null;
  recipients: number | null;
  /** Blast row of a day with a dynamic campaign: both audiences, because leads and revenue cover both. */
  attribution_recipients?: number | null;
  unique_opens: number | null;
  unique_clicks: number | null;
  /** 1 = CTOR-Scanner-Fingerprint (Klicks/Öffnungen > 25% bei ≥50 Opens) —
   *  Klickzahl unbrauchbar, Rest der Zeile normal. Backend-berechnet
   *  (helpers/scannerClickGuard.ts). */
  scanner_suspect?: number;
  unsubscribed: number | null;
  // Attribution joins (verlag_affiliates / verlag_sd_registrations by
  // mc_campaign_id). NULL = no row found (typical for asset_analysis sends).
  // Revenue arrives as `decimal(10,2)` from MariaDB → string via mysql2.
  leads: number | null;
  revenue: string | number | null;
  sois: number | null;
}

interface ClientRow {
  client_code: string;
  client_name: string;
  active: number;
  mailchimp_client: string | null;
  /** 0 = shadow-only (kme) — appears in labels/filters but is not part of
   *  the 19:30 picker and cannot be manually triggered. */
  external_sam_enabled: number;
}

/** Sub-tab slugs for ?subtab= — shareable deep links (see index.tsx docblock). */
const SUBTAB_SLUGS = ['planned', 'history', 'scoring', 'manual'];

const ExternalTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = Math.max(0, SUBTAB_SLUGS.indexOf(searchParams.get('subtab') || 'planned'));
  const [planned, setPlanned] = useState<PlannedRow[]>([]);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [runClient, setRunClient] = useState('');
  const [runSendDate, setRunSendDate] = useState('');
  const [running, setRunning] = useState(false);

  const [preview, setPreview] = useState<SamHtmlPreviewData | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // History filters — all in-memory; the API still returns the full window
  // because the `days` selector controls the backend window and these filters
  // slice the response. Empty arrays / 'all' = no constraint.
  const [filterClients, setFilterClients] = useState<string[]>([]);
  const [filterCustomers, setFilterCustomers] = useState<string[]>([]);
  const [filterMaterial, setFilterMaterial] = useState<'all' | 'sam' | 'asset_analysis'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'sent' | 'cancelled' | 'failed'>('all');

  // Reactivation-inclusion toggle (2026-07-02). Reactivation probes are still
  // External-SAM pipeline sends but land on the Inaktiv-audience via a separate
  // cron (reactivation_all_clients_daily) and don't consume the regular SAM
  // cooldown chain (buildSelectionSql filters is_reactivation=0). Hidden by
  // default so the Planned/Historie views read as "regular pipeline"; opt-in
  // switch merges reactivation rows back in for a mixed view.
  const [includeReactivation, setIncludeReactivation] = useState<boolean>(false);
  const matchesReactivation = useCallback(
    (isReactivation: number) => includeReactivation || !isReactivation,
    [includeReactivation],
  );

  // Distinct sets for filter dropdowns, derived from loaded history.
  const customerOptions = useMemo(
    () => Array.from(new Set(history.map((r) => r.customer_id).filter(Boolean) as string[])).sort(),
    [history],
  );
  const clientOptions = useMemo(
    () => Array.from(new Set(history.map((r) => r.client_code).filter(Boolean))).sort(),
    [history],
  );

  const filteredHistory = useMemo(() => {
    return history.filter((r) => {
      if (!matchesReactivation(r.is_reactivation)) return false;
      if (filterClients.length > 0 && !filterClients.includes(r.client_code)) return false;
      if (
        filterCustomers.length > 0 &&
        (!r.customer_id || !filterCustomers.includes(r.customer_id))
      ) {
        return false;
      }
      if (filterMaterial !== 'all') {
        // SAM rows have material_type='sam' OR NULL (legacy); AA rows have 'asset_analysis'.
        const t = r.material_type ?? 'sam';
        if (t !== filterMaterial) return false;
      }
      if (filterStatus !== 'all' && r.status !== filterStatus) return false;
      return true;
    });
  }, [history, matchesReactivation, filterClients, filterCustomers, filterMaterial, filterStatus]);

  const filteredPlanned = useMemo(
    () => planned.filter((r) => matchesReactivation(r.is_reactivation)),
    [planned, matchesReactivation],
  );

  const loadPlanned = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/api/adserver/external-sams/planned');
      setPlanned(data?.data ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/api/adserver/external-sams/history?days=60');
      setHistory(data?.data ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadClients = useCallback(async () => {
    try {
      const { data } = await api.get('/api/adserver/external-sams/clients');
      setClients(data?.data ?? []);
    } catch (e) {
      console.error('clients load failed', e);
    }
  }, []);

  useEffect(() => {
    loadClients();
    loadPlanned();
  }, [loadClients, loadPlanned]);

  useEffect(() => {
    if (tab === 1) loadHistory();
  }, [tab, loadHistory]);

  const handlePreview = async (id: number) => {
    setError(null);
    try {
      const { data } = await api.get(`/api/adserver/external-sams/preview/${id}`);
      if (data?.success) {
        setPreview(data.data as SamHtmlPreviewData);
        setPreviewOpen(true);
      } else {
        setError(data?.error ?? 'Vorschau fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleCancel = async (id: number) => {
    if (!window.confirm(`Geplanten Versand #${id} stornieren? Mailchimp-Draft wird gelöscht.`)) {
      return;
    }
    setError(null);
    setInfo(null);
    try {
      const { data } = await api.post(`/api/adserver/external-sams/cancel/${id}`, {});
      if (data?.success) {
        setInfo(`Versand #${id} storniert.`);
        loadPlanned();
      } else {
        setError(data?.error ?? 'Cancel fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleRunNow = async () => {
    setError(null);
    setInfo(null);
    setRunning(true);
    try {
      const body: { client?: string; sendDate?: string } = {};
      if (runClient) body.client = runClient;
      if (runSendDate) body.sendDate = runSendDate;
      const { data } = await api.post('/api/adserver/external-sams/run-now', body);
      if (data?.success) {
        const summary = data.summary
          ? `total=${data.summary.total} scheduled=${data.summary.scheduled} skipped=${data.summary.skipped} failed=${data.summary.failed}`
          : data.result
            ? `${data.result.clientCode}: ${data.result.status}${data.result.skipReason ? ` (${data.result.skipReason})` : ''}`
            : 'OK';
        setInfo(`Pipeline-Lauf abgeschlossen: ${summary}`);
        loadPlanned();
      } else {
        setError(data?.error ?? 'Run-Now fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setSearchParams({ tab: 'external', subtab: SUBTAB_SLUGS[v] })}
        sx={{ mb: 2 }}
      >
        <Tab label="Geplante Versendungen (7 Tage)" />
        <Tab label="Historie (60 Tage)" />
        <Tab label="Scoring & Auswahl" />
        <Tab label="Manueller Lauf" />
      </Tabs>

      {(tab === 0 || tab === 1) && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeReactivation}
                onChange={(_, checked) => setIncludeReactivation(checked)}
              />
            }
            label="Reaktivierungs-Kampagnen einblenden"
          />
          <Typography variant="caption" sx={{ color: '#555' }}>
            {includeReactivation
              ? 'Reaktivierungs-Probes (Inaktiv-Cohort A) sind mit eingeblendet. Zählen NICHT auf den regulären SAM-Cooldown.'
              : 'Nur reguläre SAM-Kampagnen. Reaktivierungs-Probes ausgeblendet.'}
          </Typography>
        </Box>
      )}

      {tab === 0 && (
        <Box>
          {loading ? (
            <CircularProgress />
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      'Sendezeit',
                      'Client',
                      'Status',
                      'SAM',
                      'Produkt',
                      'Asset',
                      'Kunde',
                      'MC Campaign',
                      'Aktion',
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          backgroundColor: '#e0e0e0 !important',
                          color: 'black',
                          fontWeight: 'bold',
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPlanned.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        Keine geplanten Versendungen in den nächsten 7 Tagen.
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredPlanned.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{formatBerlin(row.send_date)}</TableCell>
                      <TableCell>{clientLabel(row.client_code)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={row.status} color={statusChipColor(row.status)} />
                        {row.dup_send ? <Chip size="small" label="Dup" sx={{ ml: 0.5 }} /> : null}
                        {row.inactive_send ? (
                          <Chip size="small" label="Inaktiv" sx={{ ml: 0.5 }} />
                        ) : null}
                        {row.shadow_of_id != null || row.client_code === 'kme' ? (
                          <Chip size="small" label="Shadow" color="info" sx={{ ml: 0.5 }} />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        #{row.sam_id}
                        {row.sam_subject ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {row.sam_subject}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        #{row.product_id}
                        {row.product_name ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {row.product_name}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {row.material_type === 'asset_analysis' && row.asset_isin ? (
                          <>
                            <code>{row.asset_isin}</code>
                            {row.asset_name ? (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {row.asset_name}
                              </Typography>
                            ) : null}
                          </>
                        ) : (
                          '–'
                        )}
                      </TableCell>
                      <TableCell>{row.customer_id ?? '–'}</TableCell>
                      <TableCell>{row.mc_campaign_id ?? '–'}</TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => handlePreview(row.id)}>
                          Vorschau
                        </Button>
                        {row.status === 'scheduled' && (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => handleCancel(row.id)}
                            sx={{ ml: 1 }}
                          >
                            Stornieren
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          {loading ? (
            <CircularProgress />
          ) : (
            <>
              <Stack
                direction="row"
                gap={2}
                flexWrap="wrap"
                alignItems="center"
                sx={{ mb: 2, p: 1.5, backgroundColor: '#ffffff', borderRadius: 1 }}
              >
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="ext-client-filter-label">Client</InputLabel>
                  <Select
                    labelId="ext-client-filter-label"
                    multiple
                    value={filterClients}
                    onChange={(e) =>
                      setFilterClients(
                        typeof e.target.value === 'string'
                          ? e.target.value.split(',')
                          : (e.target.value as string[]),
                      )
                    }
                    input={<OutlinedInput label="Client" />}
                    renderValue={(selected) =>
                      (selected as string[]).map((s) => clientLabel(s)).join(', ')
                    }
                  >
                    {clientOptions.map((c) => (
                      <MenuItem key={c} value={c}>
                        {clientLabel(c)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="ext-customer-filter-label">Kunde</InputLabel>
                  <Select
                    labelId="ext-customer-filter-label"
                    multiple
                    value={filterCustomers}
                    onChange={(e) =>
                      setFilterCustomers(
                        typeof e.target.value === 'string'
                          ? e.target.value.split(',')
                          : (e.target.value as string[]),
                      )
                    }
                    input={<OutlinedInput label="Kunde" />}
                    renderValue={(selected) => (selected as string[]).join(', ')}
                  >
                    {customerOptions.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={filterMaterial}
                  onChange={(_e, v) => v && setFilterMaterial(v)}
                  aria-label="Material-Typ"
                >
                  <ToggleButton value="all">Alle</ToggleButton>
                  <ToggleButton value="sam">SAM</ToggleButton>
                  <ToggleButton value="asset_analysis">AA</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={filterStatus}
                  onChange={(_e, v) => v && setFilterStatus(v)}
                  aria-label="Status"
                >
                  <ToggleButton value="all">Alle</ToggleButton>
                  <ToggleButton value="sent">Sent</ToggleButton>
                  <ToggleButton value="cancelled">Cancelled</ToggleButton>
                  <ToggleButton value="failed">Failed</ToggleButton>
                </ToggleButtonGroup>
                {(filterClients.length > 0 ||
                  filterCustomers.length > 0 ||
                  filterMaterial !== 'all' ||
                  filterStatus !== 'all') && (
                  <Button
                    size="small"
                    onClick={() => {
                      setFilterClients([]);
                      setFilterCustomers([]);
                      setFilterMaterial('all');
                      setFilterStatus('all');
                    }}
                  >
                    Zurücksetzen
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {filteredHistory.length} / {history.length} Sendungen
                </Typography>
              </Stack>
              {/* stats_excluded rows (incident sends, e.g. DNV LP outage) stay
                  visible in the table below but must not drag the windowed
                  aggregates — their zero-lead data is structurally invalid. */}
              <HistoryKpiCards
                rows={filteredHistory.filter((r) => Number(r.stats_excluded) !== 1)}
              />
              <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 700 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {[
                        'Sendezeit',
                        'Client',
                        'Typ',
                        'Status',
                        'SAM',
                        'Asset',
                        'Empf.',
                        'Opens',
                        'Clicks',
                        'SOI',
                        'Leads',
                        'Revenue',
                        'ETKP',
                        'Unsubs',
                        'Aktion',
                      ].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredHistory.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={15} align="center">
                          {history.length === 0 ? 'Keine Historie.' : 'Keine Treffer für Filter.'}
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredHistory.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatBerlin(row.send_date)}
                        </TableCell>
                        <TableCell>{clientLabel(row.client_code)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={renderType(row.material_type)}
                            color={row.material_type === 'asset_analysis' ? 'info' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.status}
                            color={statusChipColor(row.status)}
                          />
                          {row.shadow_of_id != null || row.client_code === 'kme' ? (
                            <Chip size="small" label="Shadow" color="info" sx={{ ml: 0.5 }} />
                          ) : null}
                          {Number(row.stats_excluded) === 1 ? (
                            <Tooltip
                              title={`Aus Statistiken ausgeschlossen: ${row.stats_excluded_reason ?? 'Incident'}`}
                            >
                              <Chip size="small" label="Ausfall" color="warning" sx={{ ml: 0.5 }} />
                            </Tooltip>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          #{row.sam_id}
                          {row.sam_subject ? (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {row.sam_subject}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {row.material_type === 'asset_analysis' && row.asset_isin ? (
                            <>
                              <code>{row.asset_isin}</code>
                              {row.asset_name ? (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                >
                                  {row.asset_name}
                                </Typography>
                              ) : null}
                            </>
                          ) : (
                            '–'
                          )}
                        </TableCell>
                        <TableCell align="right">{renderInt(row.recipients)}</TableCell>
                        <TableCell align="right">
                          {renderInt(row.unique_opens)}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {renderRate(row.unique_opens, row.recipients)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {row.scanner_suspect === 1 ? (
                            <Tooltip title="Scanner-Verdacht: Klicks/Öffnungen > 25% — Klickzahl durch Security-Scanner aufgebläht, nicht belastbar">
                              <span>⚠ {renderInt(row.unique_clicks)}</span>
                            </Tooltip>
                          ) : (
                            renderInt(row.unique_clicks)
                          )}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {renderRate(row.unique_clicks, row.recipients)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{renderInt(row.sois)}</TableCell>
                        <TableCell align="right">
                          {renderInt(row.leads)}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {renderRate(row.leads, row.attribution_recipients ?? row.recipients, 2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{renderEur(row.revenue)}</TableCell>
                        <TableCell align="right">
                          {renderEtkp(row.revenue, row.attribution_recipients ?? row.recipients)}
                        </TableCell>
                        <TableCell align="right">
                          {renderInt(row.unsubscribed)}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {renderRate(row.unsubscribed, row.recipients, 2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => handlePreview(row.id)}>
                            Vorschau
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      )}

      {tab === 2 && <ExternalScoringTab />}

      {tab === 3 && (
        <Box maxWidth="600px">
          <Typography variant="body2" sx={{ mb: 2 }}>
            Manueller Trigger der Pipeline. Ohne Client-Auswahl wird für ALLE aktiven externen
            Clients (
            {clients
              .filter((c) => c.external_sam_enabled === 1)
              .map((c) => c.client_code)
              .join(', ') || '…'}
            ) der morgige 06:00-Versand geplant. Mit Client-Auswahl nur dieser. Sendedatum optional
            (Default: morgen). KME erhält ausschließlich den 08:00-Shadow des DNV-SAMs und ist
            nicht manuell auslösbar.
          </Typography>
          <Box display="flex" gap={2} alignItems="center" sx={{ mb: 2 }}>
            <Select
              size="small"
              displayEmpty
              value={runClient}
              onChange={(e) => setRunClient(e.target.value as string)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Alle aktiven Clients</MenuItem>
              {clients
                .filter((c) => c.external_sam_enabled === 1)
                .map((c) => (
                  <MenuItem key={c.client_code} value={c.client_code}>
                    {c.client_code.toUpperCase()} — {c.client_name}
                  </MenuItem>
                ))}
            </Select>
            <TextField
              size="small"
              type="date"
              label="Sendedatum"
              InputLabelProps={{ shrink: true }}
              value={runSendDate}
              onChange={(e) => setRunSendDate(e.target.value)}
            />
            <Button variant="contained" onClick={handleRunNow} disabled={running}>
              {running ? 'läuft…' : 'Jetzt ausführen'}
            </Button>
          </Box>
        </Box>
      )}

      <SamHtmlPreviewDialog
        open={previewOpen}
        preview={preview}
        onClose={() => setPreviewOpen(false)}
      />
    </Box>
  );
};

export default ExternalTab;
